import { NotificationType, useNotifications } from '@gr/portal/contexts/NotificationContext';
import { CampaignTypeEnum, MessageTypeEnum } from '@gr/shared/enums';
import { ICalculateLeadsRequest, ICampaignTypeWithAreaCodes, IContactListEntity, IHttpResponse } from '@gr/shared/models';
import { Button, ButtonVariantEnum, CalculateIcon } from '@Wonder-Cave/ui';
import { useRef, useState } from 'react';
import { axiosPost } from '../../authAxios';
import { IDropdownValue } from '../shared/Form/Dropdown';
import NewCampaignTypeDropdown from '../shared/Form/Dropdowns/NewCampaignTypeDropdown';
import NewMessageTypeDropdown from '../shared/Form/Dropdowns/NewMessageType';
import OldClientDropdown from '../shared/Form/Dropdowns/OldClientDropdown';
import { convertEnumToReadableString } from '@gr/portal/providers/utility.provider';
import useCampaignTypesForClient from '@gr/portal/hooks/useCampaignTypesForClient';

interface IContactListLeadGenerationProps {
  selectedItem: IContactListEntity | undefined;
  totalContactCount: number;
}

const ContactListLeadGeneration = ({ selectedItem, totalContactCount }: IContactListLeadGenerationProps) => {
  const { addNotification } = useNotifications();
  const [showLeadsCount, setShowLeadsCount] = useState(false);
  const [leadsCount, setLeadsCount] = useState(0);
  const [calculateLoading, setCalculateLoading] = useState(false);
  // Generation counter: bumped when a calculation starts and when the selection changes, so a
  // response arriving after the user has moved on is discarded rather than displayed.
  const leadsRequestIdRef = useRef(0);

  const [selectedClient, setSelectedClient] = useState<IDropdownValue>();
  const [selectedCampaignType, setSelectedCampaignType] = useState<CampaignTypeEnum>();
  const [selectedMessageType, setSelectedMessageType] = useState<IDropdownValue>();
  const [collapseLeadGeneration, setCollapseLeadGeneration] = useState(false);

  // Campaign types
  const { data } = useCampaignTypesForClient(selectedClient?.value, selectedMessageType?.value);
  const campaignTypes: ICampaignTypeWithAreaCodes[] = data?.types ?? [];
  const dropdownValues: IDropdownValue[] = campaignTypes.map((ct) => ({
    label: ct.campaignType === 'GOTV' ? 'GOTV' : convertEnumToReadableString(ct.campaignType), // We want GOTV to remain full caps
    value: ct.campaignType,
  }));

  const calculateLeads = async () => {
    // The dropdowns stay enabled while a calculation runs, so the selection can change before the
    // response lands. Claim a generation up front and only write state if it is still current.
    const requestId = ++leadsRequestIdRef.current;
    setCalculateLoading(true);
    try {
      const request: ICalculateLeadsRequest = {
        contactListIds: [selectedItem!.id],
        clientId: selectedClient?.value,
        campaignType: selectedCampaignType!,
        campaignMessageType: selectedMessageType?.value!,
      };

      // Explicit timeout: the shared axios instance sets none, so a hung request would never
      // settle, the finally would never run, and calculateLoading would disable the button for
      // the rest of the session. Sits above API Gateway's 29s cutoff so the server's own
      // timeout response wins whenever there is one.
      const {
        data: { data },
      } = await axiosPost<IHttpResponse<number>>('/contact-lists-calculate-leads', request, { timeout: 35_000 });

      if (requestId !== leadsRequestIdRef.current) {
        return;
      }

      setLeadsCount(data);
      // Revealed on success only, never from the finally: revealing it there showed leadsCount at
      // its useState(0) initial value on a first failure, fabricating an authoritative "0 leads"
      // that outlived the failure toast.
      setShowLeadsCount(true);
    } catch (err: any) {
      console.error(err);

      // Superseded: the user changed the selection while this was in flight, so the failure refers
      // to a calculation they have already abandoned. Logged above, but not surfaced.
      if (requestId !== leadsRequestIdRef.current) {
        return;
      }

      // A gateway timeout carries no CORS headers, so err.response is undefined there — fall back
      // rather than reading it unguarded. Any count already on screen is deliberately left as it is.
      addNotification({ content: err?.response?.data?.message ?? 'Failed to calculate leads. Please try again.', type: NotificationType.FAILURE });
    } finally {
      // Only the current generation owns the flag — a superseded calculation must not clear it,
      // because either invalidateLeadsCount already did, or a newer calculation is now running
      // and still needs the spinner.
      if (requestId === leadsRequestIdRef.current) {
        setCalculateLoading(false);
      }
    }
  };

  // Any change to an input that feeds the request invalidates the displayed count. Bumping the
  // generation also discards a response still in flight for the previous selection.
  const invalidateLeadsCount = () => {
    leadsRequestIdRef.current += 1;
    setLeadsCount(0);
    setShowLeadsCount(false);
    // Release the button immediately. Any calculation still in flight is now superseded and its
    // result will be discarded, so there is nothing to wait for.
    setCalculateLoading(false);
  };

  const handleClientSelected = (newValue: IDropdownValue | undefined) => {
    setSelectedClient(newValue);
    setSelectedCampaignType(undefined);
    invalidateLeadsCount();
  };

  return (
    <><div className="mt-6">
      <div className="flex flex-wrap w-full pt-6 mt-4 space-x-10 shadow shrink bg-slate-200 dark:bg-slate-700 dark:border-2 dark:border-slate-800 sm:rounded-lg">
        <div className="w-1/3 pl-10">
          <OldClientDropdown
            value={selectedClient}
            placeholder='Search for a client...'
            onChange={(newValue) => {
              handleClientSelected(newValue as IDropdownValue);
            }} />

        </div>
        <div className="w-1/6">
          <NewMessageTypeDropdown
            value={selectedMessageType?.value}
            onChange={(newValue) => {
              setSelectedMessageType(newValue);
              invalidateLeadsCount();
            }}
            options={[
              { label: 'SMS', value: MessageTypeEnum.SMS },
              { label: 'MMS', value: MessageTypeEnum.MMS }
            ]} showError={false} />
        </div>
        <div className="w-1/3 pr-10">
          <NewCampaignTypeDropdown
            placeholder='Select'
            value={selectedCampaignType}
            options={dropdownValues}
            onChange={(newValue: IDropdownValue | undefined) => {
              setSelectedCampaignType(newValue?.value);
              invalidateLeadsCount();
            }}
          />
        </div>
        <div className="flex w-full">
          <Button
            type="button"
            className='mb-10 mr-4'
            onClick={calculateLeads}
            variant={ButtonVariantEnum.GREEN}
            disabled={!selectedClient || !selectedCampaignType || !selectedMessageType || !selectedItem || calculateLoading}
            trailingIcon={
              <div className={calculateLoading ? 'spin' : ''}>
                <CalculateIcon />
              </div>
            }
            text="Calculate Leads" />
          {!calculateLoading && showLeadsCount && (
            <span className="inline-flex items-center ml-4">{leadsCount.toLocaleString()} leads</span>
          )}
        </div>
      </div>



    </div></>
  );
};

export default ContactListLeadGeneration;
